/**
 * Centralized localStorage key registry
 *
 * Benefits:
 * - Single source of truth for all storage keys
 * - Type-safe key references
 * - Easy to find all storage usage
 * - Prevents key naming conflicts
 * - Makes migration/renaming easier
 *
 * Naming Convention:
 * - Global user preferences: 'user:{preference}'
 * - Per-organization data: 'org:{organizationId}:{key}'
 * - Cache data: 'cache:{type}:{id}'
 * - Temporary data: 'temp:{feature}'
 */

/**
 * User preference keys (global, not organization-specific)
 */
export const USER_KEYS = {
  // Theme preference (light, dark, system)
  THEME: 'themePreference',

  // Language/locale preference (da, en)
  LOCALE: 'locale',

  // Negotiator display mode (emoji, temp, color, free)
  NEGOTIATOR_DISPLAY_MODE: 'negotiatorDisplayMode',
} as const;

/**
 * Legacy keys (deprecated, will be migrated)
 */
export const LEGACY_KEYS = {
  // Old dark mode key (boolean) - migrated to USER_KEYS.THEME
  DARK_MODE: 'darkMode',

  // Old organization selection key - migrated to 'selected_organization_id'
  SELECTED_CLUB_ID: 'selected_club_id',
} as const;

/**
 * Organization-related keys
 */
export const ORG_KEYS = {
  // Currently selected organization ID
  SELECTED_ORGANIZATION: 'selected_organization_id',

  /**
   * Generate per-organization negotiator mode key
   * @param organizationId Organization ID
   * @returns Storage key for negotiator mode
   */
  negotiatorMode: (organizationId: string) => `negotiator_${organizationId}`,

  /**
   * Generate per-organization reports selection key
   * @param organizationId Organization ID
   * @returns Storage key for reports selection
   */
  reportsSelection: (organizationId: string) => `reports-selected-valuations-${organizationId}`,

  /**
   * Generate per-organization reports insights cache key
   * @param organizationId Organization ID
   * @returns Storage key for reports insights
   */
  reportsInsights: (organizationId: string) => `reports-insights-${organizationId}`,

  /**
   * Generate per-organization social deepdive collapsed platforms key
   * @param organizationId Organization ID
   * @returns Storage key for collapsed platforms
   */
  socialDeepDiveCollapsed: (organizationId: string) =>
    `social-deepdive-collapsed-${organizationId}`,
} as const;

/**
 * Cache keys (temporary data with expiration)
 */
export const CACHE_KEYS = {
  /**
   * Generate valuation cache key
   * @param valuationId Valuation ID
   * @returns Storage key for valuation cache
   */
  valuation: (valuationId: string) => `valuation-${valuationId}-cache`,
} as const;

/**
 * Temporary data keys (form drafts, onboarding state, etc.)
 */
export const TEMP_KEYS = {
  // Onboarding form draft data
  ONBOARDING_DRAFT: 'onboarding_draft',
} as const;

/**
 * All storage keys combined (for validation, debugging, etc.)
 */
export const ALL_STORAGE_KEYS = {
  USER: USER_KEYS,
  ORG: ORG_KEYS,
  CACHE: CACHE_KEYS,
  TEMP: TEMP_KEYS,
  LEGACY: LEGACY_KEYS,
} as const;

/**
 * Storage key patterns (for pattern matching, cleanup, etc.)
 */
export const STORAGE_PATTERNS = {
  // All negotiator mode keys
  NEGOTIATOR_MODE: /^negotiator_[a-f0-9-]+$/,

  // All reports-related keys
  REPORTS: /^reports-(selected-valuations|insights)-[a-f0-9-]+$/,

  // All social deepdive keys
  SOCIAL_DEEPDIVE: /^social-deepdive-collapsed-[a-f0-9-]+$/,

  // All valuation cache keys
  VALUATION_CACHE: /^valuation-[a-f0-9-]+-cache$/,
} as const;

/**
 * Keys to preserve during logout cleanup
 * These are user preferences that should persist across sessions
 */
export const PRESERVE_ON_LOGOUT = [USER_KEYS.THEME, USER_KEYS.LOCALE] as const;
